import { ApolloClient, ApolloProvider, InMemoryCache, from } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { createTheme } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { createUploadLink } from 'apollo-upload-client';
import React from 'react';
import { createRoot } from 'react-dom/client';

import { getToken, logout } from 'utils/auth';
import paginationHelper from 'utils/pagination-helper';
import { captureApolloError, initSentry } from 'utils/sentry';

import './index.scss';
import reportWebVitals from './reportWebVitals';
import App from './routes';

initSentry();

const httpLink = createUploadLink({
  uri: process.env.REACT_APP_BACKEND,
});

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ extensions }) => {
      if (
        extensions &&
        (extensions.code === 'AUTHENTICATION_ERROR' || extensions.code === 'UNAUTHENTICATED')
      ) {
        logout();
      }

      // To avoid login error logs
      if (!extensions || extensions.code !== 'USER-AUTH-EXCEPTION') {
        captureApolloError({ graphQLErrors }, operation.operationName, operation.variables);
      }
    });
  }

  if (networkError) {
    console.log(`[Network error]: ${networkError}`);
    captureApolloError({ networkError }, operation.operationName, operation.variables);
  }
});

const authLink = setContext((_, { headers }) => {
  // get the authentication token from local storage if it exists
  // const token = localStorage.getItem('token');
  // return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      authorization: getToken(),
      'Apollo-Require-Preflight': 'true',
    },
  };
});

const client = new ApolloClient({
  link: from([errorLink, authLink.concat(httpLink)]),
  connectToDevTools: process.env.NODE_ENV !== 'production',
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          users: paginationHelper(),
          departments: paginationHelper(),
          employees: paginationHelper(),
        },
      },
      DepartmentType: {
        fields: {
          users: paginationHelper(),
          identityCards: paginationHelper(),
          printSessions: paginationHelper(),
          designations: paginationHelper(),
        },
      },
      IdentityCardType: {
        fields: {
          scanAudits: paginationHelper(),
        },
      },
    },
  }),
});

const theme = createTheme({
  palette: {
    primary: {
      main: '#fa9602',
    },
  },
  typography: {
    fontFamily: `'Inter', sans-serif`,
  },
});

const container = document.getElementById('root');
const root = createRoot(container!);

root.render(
  <React.StrictMode>
    <ThemeProvider theme={theme}>
      <ApolloProvider client={client}>
        <App />
      </ApolloProvider>
    </ThemeProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
